import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { clientConfig } from "../../awsClientConfig";
import { userProfileData } from "../../Common/profileInfo";
import { GetLeadsByPartner } from "../../queries/Leads";
import ReferralListTable from "./referral-list-table";
import PartnerClientInfo from "../../pages/partner-client-info";
import { getColumnConfigByTab } from "./column-config";
import { TransformedLead, TabType } from "./types";

interface PartnerReferralListProps {
  initialFilter: TabType;
}

const statusMap: Record<string, string> = {
  "Payment Issue": "PaymentHold",
  "Cancelled": "Canceled",
};

const MyReferrals = ({ initialFilter }: PartnerReferralListProps) => {
  const client = new clientConfig();
  const fetchIdRef = useRef(0);

  const [leads, setLeads] = useState<TransformedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState<TabType>(initialFilter);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<{ key: string; direction: 'ascending' | 'descending' }>({
    key: "ReferredAt",
    direction: "descending",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    fetchLeads(currentTab, currentPage, rowsPerPage, searchTerm);
  }, [currentTab, currentPage, rowsPerPage, searchTerm, sorting]);

  const transformLeadData = (apiData: any): TransformedLead[] => {
    return apiData.map((lead: any): TransformedLead => ({
      id: lead.LeadId,
      name: `${lead.FirstName} ${lead.LastName}`,
      email: lead.Email,
      referredDate: lead.ReferredAt,
      enrolledDate: lead.EnrolledAt,
      lastReportDate: lead.LastTaxRiskReportDate,
      status: lead.LeadStatus,
      alertLevel: lead.TaxRiskReportRiskLevel || "-",
      hasAlert: lead.AdditionalServicesAlert ?? false,
      services: lead.ActiveServices ?? [],
      planType: lead.PlanType ?? null,
      originalData: lead,
      cancelledDate: lead.CanceledAt,
      lastFailedPaymentValue: lead.LastPaymentFailureAmount || "",
      lastSuccessfulPaymentDate: lead.LastPaymentSuccessDate || "",
    }));
  };

  const fetchLeads = async (
    tabFilter: TabType = currentTab,
    page: number = 0,
    limit: number = rowsPerPage,
    searchValue: string = searchTerm
  ) => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);

    try {
      const partnerId = await userProfileData();
      const leadStatus = tabFilter === "All" ? null : statusMap[tabFilter] || tabFilter;

      const variables: any = {
        PartnerId: partnerId,
        Limit: limit,
        NextToken: page + 1,
        LeadStatus: leadStatus,
        ...(searchValue ? { Search: searchValue } : {}),
        ...(sorting.key ? {
          LeadSortInput: {
            Field: sorting.key,
            OrderBy: sorting.direction === "ascending" ? "ASC" : "DESC",
          },
        } : {}),
      };

      const response: any = await client.query({
        query: GetLeadsByPartner,
        variables,
      });

      if (fetchId !== fetchIdRef.current) return; 

      const data = response?.data?.GetLeadsByPartner;
      if (data) {
        setLeads(transformLeadData(data.LeadSummaries || []));
        setTotalCount(data.TotalRecords);
      }
    } catch (error) {
      if (fetchId === fetchIdRef.current) {
        console.error("Error fetching leads:", error);
        setLeads([]);
      }
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  };

  const handleTabChange = (newTab: TabType) => {
    setCurrentTab(newTab);
    setCurrentPage(0);
    setSorting({ key: "ReferredAt", direction: "descending" });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const handleSortChange = (key: string, direction: 'ascending' | 'descending') => {
    setSorting({ key, direction });
    setCurrentPage(0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <ReferralListTable
        tabs={["All", "Alerts", "Pending", "Enrolled", "Payment Issue", "Cancelled"]}
        defaultTab={initialFilter}
        data={leads}
        loading={loading}
        totalCount={totalCount}
        columnConfig={getColumnConfigByTab(currentTab)}
        onTabChange={handleTabChange}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSearch={handleSearch}
        onSortChange={handleSortChange}
        detailComponent={PartnerClientInfo}
        currentSorting={sorting}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
      />
    </Box>
  );
};

export default MyReferrals;